import { useCallback } from 'react';
import type { Location, NavigateFunction } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

/** React Router / @remix-run/router stores stack depth on `history.state.idx`. */
function getHistoryDepthIdx(): number | null {
  if (typeof window === 'undefined') return null;
  const st = window.history.state as { idx?: number } | null;
  return typeof st?.idx === 'number' ? st.idx : null;
}

/**
 * Safe in-app return path from `<Link state={{ from: '/path' }} />` (must be same-origin relative).
 */
export function getLocationReturnPath(state: unknown): string | null {
  const from = (state as { from?: unknown } | null)?.from;
  if (typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')) return from;
  return null;
}

/** Current list URL (path + query) for `state.from` when opening a record. */
export function getListReturnPath(location: Pick<Location, 'pathname' | 'search'>): string {
  return `${location.pathname}${location.search}`;
}

/**
 * SPA-navigate to a record while preserving an optional return path in location state.
 */
export function openRecord(
  navigate: NavigateFunction,
  to: string,
  from?: string | null,
): void {
  if (from) {
    navigate(to, { state: { from } });
    return;
  }
  navigate(to);
}

/**
 * Prefer explicit `from` location state, then browser back when stack allows, else `fallbackPath`.
 */
export function navigateBackOrFallback(
  navigate: NavigateFunction,
  options: { explicitReturnPath?: string | null; fallbackPath: string },
): void {
  const { explicitReturnPath, fallbackPath } = options;
  if (explicitReturnPath) {
    navigate(explicitReturnPath);
    return;
  }
  const idx = getHistoryDepthIdx();
  if (idx != null && idx > 0) {
    navigate(-1);
    return;
  }
  navigate(fallbackPath);
}

/**
 * Returns a function that goes to `explicitReturnPath` when provided (via location state),
 * otherwise `navigate(-1)` when the router history stack allows, else `fallbackPath`.
 */
export function useNavigateBack(fallbackPath: string, explicitReturnPath?: string | null) {
  const navigate = useNavigate();
  return useCallback(() => {
    navigateBackOrFallback(navigate, { explicitReturnPath, fallbackPath });
  }, [navigate, fallbackPath, explicitReturnPath]);
}
